<script setup>
import JSZip from 'jszip'
import { cloneDeep, debounce, isEmpty } from 'lodash'
import { onBeforeUpdate, watch } from 'vue'
import {
  reactive,
  ref,
  useTemplateRef,
  provide,
  computed,
  nextTick,
  onMounted,
  onBeforeUnmount
} from 'vue'
import { useStore } from 'vuex'

import { useAppMenu } from '@/utils/app-menu'
import { saveFile } from '@/utils/file-saver-helpers'
import {
  addExtraSpaceForFirefox,
  alignOkrIcons,
  ignoreElementsForExport,
  replaceSvgIcons,
  replaceTaskIcons,
  useHtml2canvasWrapper
} from '@/utils/html2canvas-helpers'
import { roadmapStateInjectionKey, splitViewStateInjectionKey } from '@/utils/injection-keys'
import { clearRoadmapCache, memoizeGetCssVariableValue } from '@/utils/memoizations'
import { EXPORT_TYPES, getExportFileName, ROADMAP_KEY, roadmapBus } from '@/utils/roadmap'
import { DEFAULT_SPLIT_VIEW_STATE, SPLIT_VIEW_FOOTER_HEIGHT } from '@/utils/split-view'

import OkrTimelines from '@/components/objectives/roadmap/OkrTimelines'
import RoadmapExport from '@/components/objectives/roadmap/RoadmapExport'
import SplitView from '@/views/workspaces/_workspaceId/objectives/_okrElementsView/SplitView'

defineOptions({
  name: 'OkrElementsRoadmapPage'
})

const splitViewRef = useTemplateRef('split-view')

const requestData = expandAll => {
  splitViewRef.value.requestData(expandAll)
}

defineExpose({
  requestData
})

const emit = defineEmits({
  'load-more': null,
  'reset-search-criteria': null,
  'expand-all': null,
  'collapse-all': null,
  'update-data': null,
  'create-objective': null
})

const { html2canvasWrapper } = useHtml2canvasWrapper()

const splitViewState = reactive(cloneDeep(DEFAULT_SPLIT_VIEW_STATE))

provide(splitViewStateInjectionKey, splitViewState)

const timelinesListRef = useTemplateRef('timelines-list')
const timelinesBodyHeight = ref(0)
const timelinesBodyWidth = ref(0)

const onDataUpdated = data => {
  timelinesListRef.value.refreshPositions()
  emit('update-data', data)
}

const scrollTarget = ref(null)

const setScrollTarget = () => {
  const headCells = Object.values(roadmapState.headCells)
  const timelines = roadmapState.canvas

  if (timelines) {
    const timelinesOffsetLeft = timelines.offsetLeft
    const scrolledHeadCells = headCells
      .map(selector => {
        return {
          selector,
          boundary: selector.getBoundingClientRect()
        }
      })
      .filter(item => {
        return item.boundary.x <= timelinesOffsetLeft
      })

    const headCellForScrollTarget = scrolledHeadCells.reduce((acc, val) => {
      const accX = acc.boundary?.x || -Infinity
      const valX = val.boundary.x
      acc = accX > valX ? acc : val
      return acc
    }, {})

    if (!isEmpty(headCellForScrollTarget)) {
      scrollTarget.value = headCellForScrollTarget
    }
  }
}

const scrollToScrollTarget = () => {
  if (!isEmpty(scrollTarget.value)) {
    const { selector, boundary } = scrollTarget.value
    const { right, width } = boundary
    const timelines = roadmapState.canvas
    timelines.scrollLeft = selector.offsetLeft + (width - (right - timelines.offsetLeft))
  }
}

const debouncedSetScrollTarget = debounce(() => {
  setScrollTarget()
}, 400)

onMounted(() => {
  setScrollTarget()
  nextTick(() => {
    roadmapBus.on('set-scroll-target', debouncedSetScrollTarget)
  })
})

onBeforeUpdate(() => {
  debouncedSetScrollTarget()
})

const onSplitViewDOMUpdated = () => {
  if (!splitViewState.resizeContinues) {
    scrollToScrollTarget()
  }
}

onBeforeUnmount(() => {
  roadmapBus.off('set-scroll-target', debouncedSetScrollTarget)
  clearRoadmapCache()
})

const onOkrElementUpdated = () => {
  setScrollTarget()
}

const isTimelinesHighlighted = ref(false)

const calculateHighlightAreas = () => {
  const { offsetLeft } = timelinesListRef.value.$refs.timelinesWrapper
  const topPart = getTopPart()
  const HEIGHT_WITHOUT_FOOTER = `calc(100% - ${SPLIT_VIEW_FOOTER_HEIGHT}px)`
  polygonPath.value = `polygon(0 0, 100% 0, 100% ${topPart}px, ${offsetLeft}px ${topPart}px, ${offsetLeft}px ${HEIGHT_WITHOUT_FOOTER}, 100% ${HEIGHT_WITHOUT_FOOTER}, 100% 100%, 0 100%)`
  borderBounding.value = {
    top: `${topPart}px`,
    left: `${offsetLeft}px`,
    width: `calc(100% - ${offsetLeft}px)`,
    height: `calc(${HEIGHT_WITHOUT_FOOTER} - ${topPart}px)`
  }
}

watch(
  isTimelinesHighlighted,
  newValue => {
    if (newValue) {
      window.addEventListener('scroll', calculateHighlightAreas)
    } else {
      window.removeEventListener('scroll', calculateHighlightAreas)
    }
  },
  { immediate: true }
)

const pageHeaderHeight = computed(() => store.state.system.pageHeaderHeight)

const appLicenseBannerHeight = computed(() => store.state.system.appLicenseBannerHeight)

const otWrapperHeight = computed(() => store.state.system.otWrapperHeight)

const fullscreen = computed(() => store.state.system.fullscreen)

const { isMenuExpanded } = useAppMenu()

const getTopPart = () => {
  const defaultHeight = pageHeaderHeight.value + appLicenseBannerHeight.value
  if (fullscreen.value) {
    return defaultHeight
  }

  return window.scrollY >= otWrapperHeight.value
    ? defaultHeight
    : defaultHeight + otWrapperHeight.value - window.scrollY
}

const polygonPath = ref(null)
const borderBounding = ref(null)

const toggleShowArea = isShowArea => {
  isTimelinesHighlighted.value = isShowArea

  if (isShowArea) {
    calculateHighlightAreas()
  }
}

const store = useStore()

const toggleFullAppLoader = async () => {
  await store.dispatch('system/toggleFullAppLoader')
}

const getVisibleAreaExport = async () => {
  const MIN_TOP_OFFSET = 0
  const timelinesWrapper = timelinesListRef.value.$refs.timelinesWrapper
  const BORDERS_SIZE = 2

  const topPart = fullscreen.value ? MIN_TOP_OFFSET : getTopPart()

  const roadmapOffsetTop = fullscreen.value
    ? window.scrollY
    : window.scrollY - otWrapperHeight.value

  const y = roadmapOffsetTop < MIN_TOP_OFFSET ? MIN_TOP_OFFSET : roadmapOffsetTop + BORDERS_SIZE

  const baseHeight = window.innerHeight - topPart - SPLIT_VIEW_FOOTER_HEIGHT

  const height = fullscreen.value
    ? baseHeight - pageHeaderHeight.value - appLicenseBannerHeight.value
    : baseHeight

  const canvas = await html2canvasWrapper(timelinesWrapper, {
    onclone: clonedNode => {
      addExtraSpaceForFirefox(clonedNode)
      alignOkrIcons(clonedNode)
      replaceTaskIcons(clonedNode)
      replaceSvgIcons(clonedNode)
    },
    useCORS: true, // necessary to display base64 images
    imageTimeout: 0,
    height,
    y,
    ignoreElements: ignoreElementsForExport()
  })

  canvas.toBlob(blob => {
    saveFile(blob, getExportFileName({ exportType: EXPORT_TYPES.VISIBLE }))
  }, 'image/png')
}

const getFullRoadmapExport = async ({ exportType, cropSize = 1e4 }) => {
  const timelinesWrapper = timelinesListRef.value.$refs.timelinesWrapper
  const isFullExport = exportType === EXPORT_TYPES.FULL
  const needCollapseTable = !splitViewState.isTableCollapsed

  if (needCollapseTable) {
    splitViewRef.value.onCollapseExpandTable(true)
  }

  await nextTick()

  const MAX_CHUNK_SIZE = cropSize
  const TOTAL_SIZE = timelinesBodyWidth.value
  const CHUNKS_COUNT = Math.ceil(TOTAL_SIZE / MAX_CHUNK_SIZE)
  const LAST_CHUNK = TOTAL_SIZE - MAX_CHUNK_SIZE * (CHUNKS_COUNT - 1)
  const ALL_CHUNKS = Array.from({ length: CHUNKS_COUNT }).fill(MAX_CHUNK_SIZE)

  const chunks = ALL_CHUNKS.map((item, index) => {
    const isLastChunk = index === ALL_CHUNKS.length - 1
    return {
      width: isLastChunk ? LAST_CHUNK : item,
      scrollX: item * index,
      isLastChunk
    }
  })

  const DEFAULT_MENU_WIDTH = parseFloat(
    memoizeGetCssVariableValue('--ob-expanded-app-menu-width', '240px')
  )
  const COLLAPSED_MENU_WIDTH = parseFloat(
    memoizeGetCssVariableValue('--ob-collapsed-app-menu-width', '72px')
  )
  const FULLSCREEN_MENU_WIDTH = 0
  const [APP_MENU_WIDTH] = [
    fullscreen.value && FULLSCREEN_MENU_WIDTH,
    isMenuExpanded.value && DEFAULT_MENU_WIDTH,
    COLLAPSED_MENU_WIDTH
  ].filter(width => width >= 0 && width !== false)

  const IMAGE_HEIGHT = splitViewRef.value.$refs.wrapper.offsetHeight - SPLIT_VIEW_FOOTER_HEIGHT
  const TABLE_SIDE_WIDTH = 40

  const images = await Promise.all(
    chunks.map(async (item, index) => {
      const { width, scrollX, isLastChunk } = item
      const canvas = await html2canvasWrapper(timelinesWrapper, {
        onclone: clonedNode => {
          addExtraSpaceForFirefox(clonedNode)
          alignOkrIcons(clonedNode)
          replaceTaskIcons(clonedNode)
          const timelinesBody = clonedNode.querySelector('.otl-Timelines')
          const timelinesHead = clonedNode.querySelector('.otl-Head')
          if (isLastChunk) {
            // necessary to get the right side of last image instead of the left side
            timelinesBody.style.width = `${width}px`
            timelinesHead.style.width = `${width}px`
          }
          timelinesBody.scrollLeft = scrollX
          timelinesHead.scrollLeft = scrollX
        },
        useCORS: true, // necessary to display base64 images
        width,
        windowWidth: width + TABLE_SIDE_WIDTH + APP_MENU_WIDTH,
        x: 0,
        y: 0,
        height: IMAGE_HEIGHT,
        imageTimeout: 0,
        ignoreElements: ignoreElementsForExport()
      })

      const src = canvas.toDataURL('image/png')

      return {
        name: getExportFileName({ exportType, index }),
        canvas,
        src,
        // split → get only base64 data
        base64: src.split('base64,')[1],
        x: chunks[index].scrollX,
        y: 0,
        chunkWidth: width,
        chunkHeight: IMAGE_HEIGHT
      }
    })
  )

  if (isFullExport) {
    const mergedCanvas = document.createElement('canvas')
    mergedCanvas.width = TOTAL_SIZE
    mergedCanvas.height = IMAGE_HEIGHT
    const ctx = mergedCanvas.getContext('2d')

    images.forEach(({ canvas, x, chunkWidth, chunkHeight }) => {
      /**
       * @param {HTMLCanvasElement} canvas - Source chunk canvas (its native pixel size may differ from chunkWidth/chunkHeight, e.g. due to devicePixelRatio).
       * @param {number} sx - x-offset in the source canvas to start copying from.
       * @param {number} sy - y-offset in the source canvas to start copying from.
       * @param {number} sWidth - width in the source canvas to copy.
       * @param {number} sHeight - height in the source canvas to copy.
       * @param {number} dx - x-offset in the target canvas to place the chunk at.
       * @param {number} dy - y-offset in the target canvas to place the chunk at.
       * @param {number} dWidth - width to scale the chunk to on the target canvas.
       * @param {number} dHeight - height to scale the chunk to on the target canvas.
       */
      ctx.drawImage(canvas, 0, 0, canvas.width, canvas.height, x, 0, chunkWidth, chunkHeight)
    })

    mergedCanvas.toBlob(blob => {
      saveFile(blob, getExportFileName({ exportType }))
    }, 'image/png')
  } else {
    const zip = new JSZip()

    images.forEach(item => {
      const { name, base64 } = item
      zip.file(name, base64, { base64: true })
    })

    zip.generateAsync({ type: 'blob' }).then(archive => {
      saveFile(archive, getExportFileName({ isArchive: true }))
    })
  }

  if (needCollapseTable) {
    splitViewRef.value.onCollapseExpandTable(true)
  }
}

const exportRoadmap = ({ exportType, cropSize }) => {
  toggleFullAppLoader()
  // setTimeout is for avoid start of export before the loader is shown
  setTimeout(async () => {
    if (exportType === EXPORT_TYPES.VISIBLE) {
      await getVisibleAreaExport()
    } else {
      await getFullRoadmapExport({ exportType, cropSize })
    }
    await toggleFullAppLoader()
  })
}

const roadmapState = reactive({
  canvas: null,
  headCells: {},
  timelinesWidth: null,
  someTimelineInteracted: false
})

provide(roadmapStateInjectionKey, roadmapState)

const onTableResized = () => {
  if (roadmapState.canvas) {
    roadmapState.timelinesWidth = roadmapState.canvas.offsetWidth
  }
}
</script>

<template>
  <portal to="okr-toolbar-actions-left">
    <RoadmapExport @toggle-show-area="toggleShowArea" @export-roadmap="exportRoadmap" />
  </portal>

  <template v-if="isTimelinesHighlighted">
    <div class="rm-HighlightArea" />
    <div :style="borderBounding" class="rm-HighlightAreaBorder" />
  </template>

  <SplitView
    ref="split-view"
    :placement="ROADMAP_KEY"
    show-filter-buttons
    @load-more="emit('load-more', $event)"
    @reset-search-criteria="emit('reset-search-criteria', $event)"
    @expand-all="emit('expand-all')"
    @collapse-all="emit('collapse-all')"
    @update-data="onDataUpdated"
    @create-objective="emit('create-objective', $event)"
    @table-resized="onTableResized"
    @on-updated-hook-invoked="onSplitViewDOMUpdated"
  >
    <template #content-panel="{ isNoElements }">
      <OkrTimelines
        v-show="!isNoElements"
        ref="timelines-list"
        v-model:timelines-body-height="timelinesBodyHeight"
        v-model:timelines-body-width="timelinesBodyWidth"
        :no-objectives="isNoElements"
        @update-elements="onDataUpdated"
        @on-okr-element-updated="onOkrElementUpdated"
        @reset-scroll-target="scrollTarget = null"
        @toggle-show-area="toggleShowArea"
        @export-roadmap="exportRoadmap"
      />
    </template>
    <template #footer-actions>
      <portal-target name="roadmap-footer" />
    </template>
  </SplitView>
</template>

<style lang="scss" scoped>
.rm-HighlightArea {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba($dark-1, 0.75);
  z-index: 11;
  clip-path: v-bind(polygonPath);
}

.rm-HighlightAreaBorder {
  position: fixed;
  border: 4px solid $grade-high-color-next;
  z-index: 12;
}
</style>
